import { Alert, InputNumber, Modal, Radio, Select, Space, Typography } from 'antd'
import { useEffect, useState } from 'react'

import { diskErrorText, formatBytes } from '@/Containers/DiskSection/DiskContainer/Hooks'

import { QUOTA_UNITS, joinQuota, splitQuota } from '../Helpers/diskAdminFormat'
import { UpdateDiskAdminQuotaTask } from '../Tasks/UpdateDiskAdminQuotaTask'

import type { DiskAdminQuotaResult } from '../Contracts/DiskAdmin'
import type { DiskAdminQuotaTargets } from '../Tasks/UpdateDiskAdminQuotaTask'
import type { QuotaUnit } from '../Helpers/diskAdminFormat'

interface QuotaModalProps {
    open: boolean
    /** Що змінюємо: «Диск Савчука А.» або «3 диски». */
    subject: string
    targets: DiskAdminQuotaTargets
    /** Зайнято (для одного диска) — щоб попередити до збереження. */
    used: number | null
    /** Поточна власна квота (`null` — за замовчуванням). */
    currentQuota: number | null
    /** Підпис варіанта «за замовчуванням»: «За замовчуванням (10 ГБ)». */
    defaultLabel: string
    /** Квота за замовчуванням, у байтах; `null` — без власної квоти (спільний простір) або ще не відома. */
    defaultQuota: number | null
    onClose: () => void
    onSaved: (result: DiskAdminQuotaResult) => void
}

/**
 * Модалка квоти (`DISK-006` §3.10): «За замовчуванням (10 ГБ)» або число з
 * одиницею. Нижче зайнятого — дозволено: бек збереже й поверне попередження
 * `below_used`, модалка покаже його вже після збереження (і заздалегідь, якщо
 * диск один і зайняте відоме).
 */
export const QuotaModal = ({
    open,
    subject,
    targets,
    used,
    currentQuota,
    defaultLabel,
    defaultQuota,
    onClose,
    onSaved,
}: QuotaModalProps) => {
    const [mode, setMode] = useState<'default' | 'custom'>('default')
    const [value, setValue] = useState<number | null>(null)
    const [unit, setUnit] = useState<QuotaUnit>('ГБ')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) return
        // Без квоти — поле власної квоти починається з 1 ГБ
        const initial = splitQuota(currentQuota ?? defaultQuota ?? 1024 ** 3)

        setMode(currentQuota === null ? 'default' : 'custom')
        setValue(initial.value)
        setUnit(initial.unit)
        setError(null)
    }, [open, currentQuota, defaultQuota])

    const quotaBytes = mode === 'default' ? null : value === null ? undefined : joinQuota(value, unit)
    const effective = quotaBytes === null ? defaultQuota : quotaBytes
    const short = used !== null && effective !== undefined && effective !== null && used > effective ? used - effective : 0

    const save = async () => {
        if (quotaBytes === undefined) return
        setSaving(true)
        setError(null)

        try {
            onSaved(await UpdateDiskAdminQuotaTask(targets, quotaBytes))
        } catch (failure) {
            setError(diskErrorText(failure, 'Не вдалося змінити квоту'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open={open}
            title="Квота диска"
            okText="Зберегти"
            cancelText="Скасувати"
            confirmLoading={saving}
            okButtonProps={{ disabled: quotaBytes === undefined }}
            onOk={() => void save()}
            onCancel={onClose}
            destroyOnHidden
        >
            <Space orientation="vertical" size={12} style={{ width: '100%' }} data-testid="disk-admin-quota-modal">
                <Typography.Text type="secondary">{subject}</Typography.Text>
                <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as 'default' | 'custom')}>
                    <Space orientation="vertical">
                        <Radio value="default">{defaultLabel}</Radio>
                        <Radio value="custom">Власна квота</Radio>
                    </Space>
                </Radio.Group>
                {mode === 'custom' ? (
                    <Space.Compact>
                        <InputNumber
                            aria-label="Квота"
                            min={0}
                            step={1}
                            value={value}
                            onChange={(next) => setValue(typeof next === 'number' ? next : null)}
                            style={{ width: 160 }}
                            data-testid="disk-admin-quota-value"
                        />
                        <Select
                            aria-label="Одиниця"
                            value={unit}
                            onChange={(next) => setUnit(next)}
                            options={QUOTA_UNITS.map((item) => ({ value: item, label: item }))}
                            style={{ width: 80 }}
                        />
                    </Space.Compact>
                ) : null}
                {short > 0 ? (
                    <Alert
                        type="warning"
                        showIcon
                        title={`Квота менша за зайняте: нові файли не завантажаться, доки не звільниться ${formatBytes(short)}`}
                    />
                ) : null}
                {error !== null ? <Alert type="error" showIcon title={error} /> : null}
            </Space>
        </Modal>
    )
}
